import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import gameService from '../../services/gameService';
import Modal from '../../components/Modal';
import Pagination from '../../components/Pagination';
import { SearchIcon, EditIcon, TrashIcon } from '../../components/Icons';

const GameCategories = () => {
  const { hasPermission } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination & Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    bo_category: '',
    description: '',
    is_active: true
  });

  const fetchCategories = useCallback(async (page = pagination.page, search = searchTerm, limit = pagination.limit) => {
    try {
      setLoading(true);
      const response = await gameService.getCategories({ page, limit, search });
      if (response.success) {
        setCategories(response.data);
        setPagination(prev => ({
          ...prev,
          page: response.pagination.page,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      } else {
        setError(response.message || 'Failed to fetch categories');
      }
    } catch (err) {
      setError('An error occurred while fetching categories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, searchTerm]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCategories(1, searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const canEdit = hasPermission('categories', 'edit');
  const canDelete = hasPermission('categories', 'delete');
  const showActions = canEdit || canDelete;

  const handlePageChange = (newPage) => {
    fetchCategories(newPage, searchTerm);
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchCategories(1, searchTerm, newLimit);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddModal = () => {
    setEditId(null);
    setFormData({ code: '', name: '', bo_category: '', description: '', is_active: true });
    setIsModalOpen(true);
  };

  const openEditModal = (category) => {
    setEditId(category._id);
    setFormData({
      code: category.code,
      name: category.name,
      bo_category: category.bo_category || '',
      description: category.description || '',
      is_active: category.is_active
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      let response;
      if (editId) {
        response = await gameService.updateCategory(editId, formData);
      } else {
        response = await gameService.createCategory(formData);
      }

      if (response.success) {
        setIsModalOpen(false);
        setFormData({ code: '', name: '', bo_category: '', description: '', is_active: true });
        setEditId(null);
        fetchCategories();
      } else {
        alert(response.message || 'Failed to save category');
      }
    } catch (err) {
      // Axios puts server error responses in err.response.data
      const serverMsg = err?.response?.data?.message;
      alert(serverMsg || err?.message || 'An error occurred while saving category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await gameService.deleteCategory(id);
      if (response.success) {
        fetchCategories();
      } else {
        alert(response.message || 'Failed to delete category');
        setLoading(false);
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      alert(serverMsg || err?.message || 'An error occurred while deleting category');
      setLoading(false);
    }
  };

  return (
    <div className="game-categories-page">
      {hasPermission('categories', 'create') && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={openAddModal}>Add Category</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div className="search-container">
          <div className="search-icon-wrapper">
            <SearchIcon />
          </div>
          <input
            type="text"
            className="search-input"
            placeholder="Search code, name, or backend category name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading && categories.length === 0 ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Loading categories...</div>
      ) : error ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table-striped" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '920px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Code</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Backend Category Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Description</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status</th>
                {showActions && <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Actions</th>}
              </tr>
            </thead>
            <tbody style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={showActions ? 6 : 5} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No categories found.</td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: 600 }}>{category.code}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{category.name}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{category.bo_category || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{category.description || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{
                        padding: '0.25rem 0.625rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: category.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: category.is_active ? 'var(--success)' : 'var(--error)'
                      }}>
                        {category.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {showActions && (
                      <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        {canEdit && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => openEditModal(category)}
                          >
                            <EditIcon size={14} />
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => handleDelete(category._id)}
                          >
                            <TrashIcon size={14} />
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            loading={loading}
            itemName="categories"
          />
        </div>
      )}

      {/* Add/Edit Category Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editId ? "Edit Category" : "Add New Category"}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Category Code</label>
            <input
              type="text"
              name="code"
              className="input"
              value={formData.code}
              onChange={handleInputChange}
              required
              placeholder="e.g. FISHING"
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Category Name</label>
            <input
              type="text"
              name="name"
              className="input"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g. Fishing"
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Backend Category Name</label>
            <input
              type="text"
              name="bo_category"
              className="input"
              value={formData.bo_category}
              onChange={handleInputChange}
              placeholder="e.g. slots"
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Description</label>
            <textarea
              name="description"
              className="input"
              style={{ minHeight: '80px', fontFamily: 'inherit' }}
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of the category"
            />
          </div>
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Is Active</span>
            <label className="switch">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              <span className="slider"></span>
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button type="button" className="btn" onClick={() => setIsModalOpen(false)} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : (editId ? 'Update Category' : 'Create Category')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GameCategories;
